import * as zod from 'zod';

/**
 * The registry of robot-scoped settings stored in the `robot_settings` table.
 *
 * One row per key, keyed by a namespaced string rather than a column, so a new
 * scalar setting is an entry here instead of a sixteenth `*_settings` table.
 *
 * The field names are the Unified Robot Configuration System's registry axes,
 * adopted now because an axis is cheap to add before anything generates
 * accessors from it and expensive afterwards. Three of that design's layers
 * exist here — the schema default (layer 1), the per-environment default
 * (layer 3) and the robot's own row — and nothing else does.
 *
 * Before adding a key, check it against the three admission rules: it changes
 * at human timescale, it is in the registry before any robot has a value for
 * it, and it is small and serializable.
 */
export interface SettingDefinition<T = unknown> {
  schema: zod.ZodType<T>;
  /** Layer 1. An unset key resolves to this, everywhere. */
  default: T;
  /** Layer 3. Resolved against NODE_ENV, above the default, below the row. */
  environmentDefaults?: { development?: T; production?: T };
  description: string;
  owner: string;
  /** Only 'robot' today. 'fleet' and 'user' are the config project's. */
  scope: 'robot';
  /**
   * 'set' is written by a person or tool; 'observed' is reported by software.
   * Only 'set' keys exist today; the write path rejects an external write to
   * anything else, which is the config project's rule for observed keys.
   */
  source: 'set' | 'observed';
  /** Reload semantics for the consumer. Only 'live' today. */
  reload: 'live' | 'safe-boundary' | 'restart';
  sensitive?: boolean;
}

/** How often the robot takes a complete cloud backup. */
export const BackupCadence = zod.enum(['never', 'daily', 'weekly']);

export type BackupCadence = zod.infer<typeof BackupCadence>;

export const SETTINGS_REGISTRY = {
  'backups.cadence': {
    schema: BackupCadence,
    // No scheduled backup until an operator picks a cadence. A complete
    // backup captures the whole robot state, and that is an opt-in per box,
    // not something every box starts doing the night it updates.
    default: 'never',
    description: 'How often the robot takes a complete cloud backup.',
    owner: 'michael',
    scope: 'robot',
    source: 'set',
    reload: 'live',
  },
} as const satisfies Record<string, SettingDefinition<any>>;

export type SettingKey = keyof typeof SETTINGS_REGISTRY;

export type SettingValue<K extends SettingKey> = zod.infer<
  (typeof SETTINGS_REGISTRY)[K]['schema']
>;

export const SETTING_KEYS = Object.keys(SETTINGS_REGISTRY) as SettingKey[];

export function isSettingKey(key: unknown): key is SettingKey {
  return typeof key === 'string' && key in SETTINGS_REGISTRY;
}

/**
 * The value a key takes when the robot has no row for it: its environment
 * default if one is declared for `environment`, otherwise its schema default.
 *
 * `environment` is passed in rather than read from `process.env` so the one
 * answer is computed on the robot and shipped to every other reader. A browser
 * bundle's NODE_ENV describes the machine that built remote-control, not the
 * box it is talking to.
 */
export function resolveSettingDefault<K extends SettingKey>(
  key: K,
  environment: string,
): SettingValue<K> {
  const definition: SettingDefinition = SETTINGS_REGISTRY[key];

  const environmentDefault = (
    definition.environmentDefaults as Record<string, unknown> | undefined
  )?.[environment];

  return (environmentDefault ?? definition.default) as SettingValue<K>;
}

/**
 * A persisted `robot_settings` row. `value` is whatever the key's own schema
 * admits, so it is unconstrained here and checked per key by the service's
 * hooks.
 */
export const SettingData = zod.object({
  id: zod.string(),
  key: zod.string(),
  value: zod.unknown(),
  updatedAt: zod.string().optional(),
});

export type SettingData = zod.infer<typeof SettingData>;

/**
 * What the settings service answers a `find` with: one entry per registry key
 * the query asked about, whether or not the robot has ever written it.
 *
 * An unwritten key comes back with `id: null` and the default resolved on the
 * robot, so every reader — in-process, botman, or a browser — sees the same
 * value without re-implementing the layers. Readers can ignore the
 * distinction; writers must not patch a null id.
 */
export interface ResolvedSetting {
  id: string | null;
  key: string;
  value: unknown;
  updatedAt: string | null;
}
